import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Sparkles, ArrowRight, Info, X, Package, CreditCard, ChevronDown } from 'lucide-react';
import { services, aLaCarteServices } from '../mockData';
import '../styles/Pricing.css';
// Flip-card styles for the "What Happens After You Book" section.
// Defined globally in WhyWeExist.css; imported here so the styles load
// when /pricing is visited directly (not just via curtain navigation).
import '../styles/WhyWeExist.css';
import Footer from '../components/Footer';
import UniversalCTA from '../components/UniversalCTA';
import SEO from '../components/SEO';
import LuxeCompassWatermark from '../components/LuxeCompassWatermark';
import HeritageHeading from '../components/HeritageHeading';

const Pricing = () => {
  const [showServiceDetails, setShowServiceDetails] = useState(null);
  // "What Happens After You Book" flip cards. Multiple cards can be
  // open at once; tapping flips a card; tapping again flips it back.
  const [flippedSteps, setFlippedSteps] = useState([]);
  const toggleFlipStep = (stepId) => {
    setFlippedSteps((prev) =>
      prev.includes(stepId) ? prev.filter((id) => id !== stepId) : [...prev, stepId]
    );
  };

  // "Tap to advance" gold ping: when a waypoint enters the viewport,
  // mark the NEXT waypoint with .is-next-up so its tag pulses softly,
  // inviting the visitor to keep scrolling along the care journey.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    const waypoints = Array.from(document.querySelectorAll('.waypoint-section'));
    if (waypoints.length < 2) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.35) {
          waypoints.forEach((w) => w.classList.remove('is-next-up'));
          const idx = waypoints.indexOf(entry.target);
          const next = waypoints[idx + 1];
          if (next) next.classList.add('is-next-up');
        }
      });
    }, { threshold: [0.35, 0.5, 0.65], rootMargin: '-15% 0px -25% 0px' });
    waypoints.forEach((w) => observer.observe(w));
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="pricing-page">
      <SEO
        title="Pricing — Transparent Medical Consulting Packages"
        description="Clear, transparent pricing for CADENCE medical consulting services. Second opinion, full case advocacy, intake-only review, and à la carte add-ons. No surprises, no hidden fees."
        path="/pricing"
      />
      {/* STANDARDIZED: Emblem section - same as other pages */}
      <section className="emblem-section" data-testid="pricing-emblem">
        <div className="container">
          <div className="emblem-wrapper">
            <img src="/cadence-gold-foil.png" alt="CADENCE Medical Consulting" className="velvet-emblem-home" />
          </div>
        </div>
      </section>

      <section className="unified-content-section">
        <div className="container">
          <LuxeCompassWatermark />
          <h1 className="unified-page-title">One-Time Consultation Pricing</h1>
        </div>
      </section>
      
      <section className="unified-quote-section">
        <div className="container">
          <p className="unified-page-quote" style={{ fontStyle: 'normal' }}>
            Clear, straightforward pricing. Perfect for specific concerns or getting a&nbsp;second&nbsp;opinion.
          </p>
        </div>
      </section>

      {/* Quick Navigation Links — 2 pills (was 3). The "Consultation
          Packages" pill was removed because the package cards render
          immediately below, making the scroll-to-self pill redundant.
          Membership Plans is now a real cross-page Link, not an
          in-page anchor (which used to land on a circular teaser). */}
      <section className="pricing-quick-nav">
        <div className="container">
          <div className="quick-nav-links">
            <button 
              onClick={() => scrollToSection('alacarte-services')} 
              className="quick-nav-btn"
              data-testid="nav-alacarte"
            >
              <Package size={18} />
              <span>Additional Services</span>
            </button>
            <Link
              to="/membership"
              className="quick-nav-btn"
              data-testid="nav-membership"
            >
              <Sparkles size={18} />
              <span>Membership Plans</span>
            </Link>
          </div>
        </div>
      </section>

      {/* One-Time Services */}
      <section className="pricing-section" id="consultation-packages">
        <div className="container">
          <div className="pricing-grid">
            {services.map((service) => (
              <div key={service.id} className={`pricing-card ${service.featured ? 'featured' : ''}`}>
                {service.featured && (
                  <div className="featured-tag">
                    <Sparkles size={16} />
                    <span>Most Popular</span>
                  </div>
                )}
                <div className="pricing-header">
                  <h3 className="heading-3">{service.title}</h3>
                  {service.subtitle && (
                    <p className="service-subtitle-pricing">{service.subtitle}</p>
                  )}
                  <div className="pricing-amount">
                    <span className="price">${service.price.toLocaleString()}</span>
                    <span className="price-type">one-time</span>
                  </div>
                  <p className="pricing-duration">{service.duration}</p>
                </div>
                <p className="service-description-pricing">{service.description}</p>
                <ul className="pricing-features">
                  {service.features.map((feature, idx) => (
                    <li key={idx}>
                      <CheckCircle size={18} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                {service.note && (
                  <p className="service-note">{service.note}</p>
                )}
                <Link to="/contact" className={service.featured ? 'btn-primary' : 'btn-secondary'}>
                  Book Now
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT HAPPENS AFTER YOU BOOK — the 3 customer-action steps
          (formerly on /cadence-promise as "Our 3-Step Process"). Lives
          here on /pricing because the buyer's question right after
          seeing a package is "OK, what do I actually do next?" 2026-02. */}
      <section className="how-it-works after-you-book" id="after-you-book" data-testid="after-you-book">
        <div className="container">
          <h2
            className="heading-1"
            style={{ textAlign: 'center', marginBottom: '0.6rem' }}
          >
            What Happens After You Book
          </h2>
          <p
            className="section-subtitle"
            style={{ textAlign: 'center', marginBottom: '2rem' }}
          >
            Three simple steps from the moment you reserve your consultation
            to the moment you walk into your next appointment with a written
            advocacy script in&nbsp;hand.
          </p>
          <div className="steps-grid three-steps flip-cards-container">
            <div
              className={`step-card flip-card ${flippedSteps.includes('step1') ? 'flipped' : ''}`}
              onClick={() => toggleFlipStep('step1')}
              data-testid="after-step-card-1"
            >
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <div className="step-number">01</div>
                  <h3 className="heading-3">Share Your Records</h3>
                  <span className="flip-tap-hint">Tap to flip</span>
                </div>
                <div className="flip-card-back">
                  <h3 className="heading-3" style={{ color: '#D4AF37' }}>Share Your Records</h3>
                  <p className="flip-detail-text" style={{ color: '#FFFFFF' }}>
                    Send records through our HIPAA-compliant portal. We handle the labs, imaging, and provider notes &mdash; you don&rsquo;t.
                  </p>
                  <span className="flip-tap-hint">Tap to flip back</span>
                </div>
              </div>
            </div>
            <div
              className={`step-card flip-card ${flippedSteps.includes('step2') ? 'flipped' : ''}`}
              onClick={() => toggleFlipStep('step2')}
              data-testid="after-step-card-2"
            >
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <div className="step-number">02</div>
                  <h3 className="heading-3">Deep Dive Analysis</h3>
                  <span className="flip-tap-hint">Tap to flip</span>
                </div>
                <div className="flip-card-back">
                  <h3 className="heading-3" style={{ color: '#D4AF37' }}>Deep Dive Analysis</h3>
                  <p className="flip-detail-text" style={{ color: '#FFFFFF' }}>
                    Military-honed review. We analyze patterns, question assumptions, and surface what others missed.
                  </p>
                  <span className="flip-tap-hint">Tap to flip back</span>
                </div>
              </div>
            </div>
            <div
              className={`step-card flip-card ${flippedSteps.includes('step3') ? 'flipped' : ''}`}
              onClick={() => toggleFlipStep('step3')}
              data-testid="after-step-card-3"
            >
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <div className="step-number">03</div>
                  <h3 className="heading-3">Your Clarity Session</h3>
                  <span className="flip-tap-hint">Tap to flip</span>
                </div>
                <div className="flip-card-back">
                  <h3 className="heading-3" style={{ color: '#D4AF37' }}>Your Clarity Session</h3>
                  <p className="flip-detail-text" style={{ color: '#FFFFFF' }}>
                    Detailed findings and a clear next-step plan. You walk away with confidence and a written path forward.
                  </p>
                  <span className="flip-tap-hint">Tap to flip back</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* À LA CARTE - CARE JOURNEY WAYPOINTS */}
      <section className="alacarte-section" id="alacarte-services">
        <div className="container">
          <HeritageHeading
            as="p"
            size="display"
            uppercase
            className="alacarte-eyebrow"
            data-testid="alacarte-eyebrow"
          >
            À LA CARTE
          </HeritageHeading>
          <h2 className="heading-2 section-title">Your Care Journey, Priced</h2>
          <p className="section-subtitle">
            Each waypoint of your visit has its own offerings. Pick what fits where you&nbsp;are. CADENCE is here for the whole&nbsp;journey.
          </p>

          {[
            { stage: 'before', tag: 'Waypoint 1 · Before the visit', title: 'Show Up Ready', blurb: "Walk in knowing what to ask, what to expect, and what's already in your record.", flip: false },
            { stage: 'during', tag: 'Waypoint 2 · During or between visits', title: 'Move Things Forward', blurb: 'Coordination, follow-through, and the strategy calls that make scattered care feel connected.', flip: true },
            { stage: 'after',  tag: 'Waypoint 3 · After the visit', title: 'Make Sense of It', blurb: 'A quick check-in to translate what just happened — and what comes next.', flip: false },
            { stage: 'bundle', tag: 'All three waypoints · Discounted', title: 'The Whole Journey', blurb: 'Before, during, and after — bundled and discounted for visitors who want every waypoint covered.', flip: false, last: true }
          ].map((wp) => {
            const items = aLaCarteServices.filter((s) => s.stage === wp.stage);
            if (items.length === 0) return null;
            return (
              <div
                key={wp.stage}
                className="waypoint-section"
                data-testid={`waypoint-${wp.stage}`}
              >
                <div className="waypoint-header">
                  <div className="waypoint-tag-row">
                    <span className="waypoint-tag">{wp.tag}</span>
                  </div>
                  <div className="waypoint-title-row">
                    <span className="waypoint-emblem" aria-hidden="true" />
                    <HeritageHeading as="h3" size="display" data-testid={`waypoint-title-${wp.stage}`}>{wp.title}</HeritageHeading>
                    <span className="waypoint-emblem waypoint-emblem-right" aria-hidden="true" />
                  </div>
                  <p className="waypoint-blurb">{wp.blurb}</p>
                  <div className="waypoint-divider" />
                </div>

                <div className="alacarte-menu" role="list">
                  {items.map((service) => {
                    const isOpen = showServiceDetails === service.id;
                    return (
                      <div
                        key={service.id}
                        className={`alacarte-row ${isOpen ? 'is-open' : ''} ${service.isBundle ? 'is-bundle' : ''}`}
                        data-testid={`alacarte-row-${service.id}`}
                        role="listitem"
                      >
                        <button
                          type="button"
                          className="alacarte-head"
                          aria-expanded={isOpen}
                          aria-controls={`alacarte-body-${service.id}`}
                          onClick={() => setShowServiceDetails(isOpen ? null : service.id)}
                          data-testid={`alacarte-toggle-${service.id}`}
                        >
                          {/* Demo B layout — title CENTERED at top, price
                              CENTERED directly underneath, chevron stays
                              absolutely positioned at the right edge.
                              The eye reads each card top-to-bottom as a
                              unit instead of scanning prices as a column. */}
                          {service.isBundle && (
                            <span className="alacarte-bundle-flag" aria-hidden="true">
                              <Package size={14} strokeWidth={2.5} />
                              <span>BUNDLE</span>
                            </span>
                          )}
                          {service.savings && (
                            <span className="alacarte-savings-pill">{service.savings}</span>
                          )}
                          <span className="alacarte-title">{service.title}</span>
                          <span className="alacarte-price-wrap">
                            <span className="alacarte-price">${service.price}</span>
                            {service.priceNote && (
                              <span className="alacarte-price-note">{service.priceNote}</span>
                            )}
                          </span>
                          <span className="alacarte-chev" aria-hidden="true">
                            <ChevronDown size={20} strokeWidth={2.5} />
                          </span>
                        </button>

                        <div
                          id={`alacarte-body-${service.id}`}
                          className="alacarte-body"
                        >
                          <div className="alacarte-body-inner">
                            <div className="alacarte-body-panel">
                              <span className="alacarte-sheen" aria-hidden="true" />
                              <p className="alacarte-description">
                                {service.description}
                              </p>

                              {service.bundleIncludes && (
                                <div className="alacarte-includes-block">
                                  <span className="alacarte-includes-label">Bundle includes</span>
                                  <ul className="alacarte-includes-list">
                                    {service.bundleIncludes.map((line, idx) => (
                                      <li key={idx}>
                                        <span className="alacarte-tick" aria-hidden="true">✓</span>
                                        <span>{line}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {service.details?.included && (
                                <div className="alacarte-includes-block">
                                  <span className="alacarte-includes-label">What&rsquo;s Included</span>
                                  <ul className="alacarte-includes-list">
                                    {service.details.included.map((line, idx) => (
                                      <li key={idx}>
                                        <span className="alacarte-tick" aria-hidden="true">✓</span>
                                        <span>{line}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {service.details?.notIncluded && (
                                <div className="alacarte-excludes-block">
                                  <span className="alacarte-excludes-label">Not Included</span>
                                  <ul className="alacarte-excludes-list">
                                    {service.details.notIncluded.map((line, idx) => (
                                      <li key={idx}>
                                        <span className="alacarte-x" aria-hidden="true">×</span>
                                        <span>{line}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              <Link
                                to="/contact"
                                className="alacarte-book-btn"
                                data-testid={`alacarte-book-${service.id}`}
                              >
                                Book This Service
                                <ArrowRight size={16} />
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {!wp.last && (
                  <div
                    className={`waypoint-connector ${wp.flip ? 'flip' : ''}`}
                    role="img"
                    aria-label="Trail to the next waypoint"
                    data-testid={`waypoint-connector-${wp.stage}`}
                  />
                )}
              </div>
            );
          })}

          <p className="waypoint-footer-note">
            CADENCE &mdash; Clinical Advocacy &middot; Decisive Empowerment &middot; Navigating Care &amp; Expertise. Your bearing, every step of the&nbsp;way.
          </p>
        </div>
      </section>

      {/* Membership CTA - Simplified without redundant pricing tiles */}
      <section className="membership-cta-section" id="membership-cta">
        <div className="container">
          <div className="membership-cta-box">
            <h2 className="heading-2">Need More Care?</h2>
            <p className="body-large">
              Our membership plans provide continuous partnership, strategy, and advocacy for managing complex health journeys.
            </p>
            <Link to="/membership" className="btn-gold">
              See Membership Plans
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs consolidated to the unified /faq page 2026-05-19 per user.
          Direct link kept here for convenience. */}
      <section className="faq-section" id="faq">
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-2" style={{ marginBottom: '1rem' }}>Have Questions?</h2>
          <p className="body-large" style={{ marginBottom: '1.5rem' }}>
            We&rsquo;ve answered the most common pricing, payment, and cancellation questions on a single FAQ page.
          </p>
          <Link to="/faq#services" className="btn-gold" data-testid="pricing-view-faqs">
            View All Pricing FAQs
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="heading-2" style={{ color: 'white' }}>Ready to Get Started?</h2>
            <p className="body-large" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Choose the service that's right for you and take the first step toward empowered healthcare.
            </p>
            <Link to="/contact" className="btn-gold">
              Book Your Consultation
            </Link>
          </div>
        </div>
      </section>

      <UniversalCTA />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Pricing;